var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);

// src/test-helpers/index.ts
var test_helpers_exports = {};
__export(test_helpers_exports, {
  IsolatedStep: () => IsolatedStep,
  IsolatedStepCompat: () => IsolatedStepCompat
});

// src/test-helpers/flows/IsolatedStep.tsx
import React2 from "react";
import { Route as Route2, Routes as Routes2 } from "react-router-dom-v6";

// src/flows/components/StepWrapper.tsx
import {
  useLocation,
  useMatch,
  useNavigate
} from "react-router-dom-v6";

// src/flows/use-flow-state.ts
import { useCallback, useContext } from "react";
import _omit from "lodash/omit";
import invariant from "tiny-invariant";

// src/flows/data.ts
import { createContext } from "react";
var FlowContext = createContext(
  void 0
);

// src/flows/use-flow-state.ts
var getStepPercent = (steps, stepName) => {
  if (steps.includes(stepName)) {
    return (steps.indexOf(stepName) + 1) / steps.length * 100;
  }
  return 0;
};
function useFlowState() {
  const flowState = useContext(FlowContext);
  invariant(flowState, "useFlowState must be used within a FlowStateProvider.");
  const { flow: stepService, basePath } = flowState;
  const sync = useCallback(
    (step) => {
      stepService.send({ type: step });
    },
    [stepService]
  );
  const updateValues = useCallback(
    (values) => {
      stepService.send({ type: "updateValues", values });
    },
    [stepService]
  );
  const goToStep = useCallback(
    (step, values) => {
      invariant(
        flowState.steps.includes(step),
        `unknown step: "${step}". Must be one of ${flowState.steps.map((s) => `"${s}"`).join(", ")} `
      );
      if (values) {
        updateValues(values);
      }
      stepService.send({ type: step, basePath });
    },
    [stepService, flowState.steps, updateValues, basePath]
  );
  const goToNextStep = useCallback(
    (values = {}) => {
      stepService.send({ type: "next", values, basePath });
    },
    [stepService, basePath]
  );
  const goToPreviousStep = useCallback(() => {
    stepService.send({ type: "previous", basePath });
  }, [stepService, basePath]);
  const buildGetStepFor = (transitionName) => {
    return () => {
      const transition3 = stepService.machine.state.value.transitions.get(transitionName);
      if (transition3) {
        return transition3[0].to;
      }
      return void 0;
    };
  };
  const progressPercent = getStepPercent(
    flowState.steps,
    flowState.currentStep
  );
  return {
    flowState: _omit(flowState, ["flow"]),
    sync,
    updateValues,
    progressPercent,
    goToStep,
    goToNextStep,
    goToPreviousStep,
    getNextStep: buildGetStepFor("next"),
    getPreviousStep: buildGetStepFor("previous")
  };
}

// src/flows/components/StepWrapper.tsx
function StepWrapper({
  children
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const match = useMatch(location.pathname);
  const state3 = useFlowState();
  const stepProps = {
    navigate,
    location,
    match,
    ...state3
  };
  return typeof children === "function" ? children(stepProps) : children;
}

// src/flows/utilities.ts
import React, { useEffect, useReducer, useRef } from "react";
import _first from "lodash/first";
import _deepEqual from "lodash/isEqual";
import _last from "lodash/last";
import { Route, Routes, useLocation as useLocation2, useNavigate as useNavigate2 } from "react-router-dom-v6";
import {
  action,
  createMachine,
  guard,
  interpret,
  reduce,
  state,
  transition
} from "robot3";
var getStepNameFromPath = (pathname, basePath) => {
  if (!basePath.endsWith("/")) basePath = `${basePath}/`;
  if (!pathname.startsWith(basePath)) return "";
  return pathname.substring(basePath.length);
};
var reducer = () => reduce((context, event) => ({
  ...context,
  ...event.values
}));
var when = (fn) => guard(fn);
var nextStep = (stepName, ...guardsOrReducers) => {
  return transition("next", stepName, ...guardsOrReducers, reducer());
};
var previousStep = (stepName, ...guardsOrReducers) => {
  return transition("previous", stepName, ...guardsOrReducers);
};
var createStep = (stepName) => {
  return {
    [stepName]: [transition("updateValues", stepName, reducer())]
  };
};
var createMachineFromConfig = (stepConfig, values, initialStep) => {
  const steps = stepConfig.map((step) => {
    return Object.keys(step)[0];
  });
  const flowConfig = steps.reduce((config, step, index) => {
    const transitions = [
      ...stepConfig[index][step],
      ...steps.map((s) => transition(s, s)),
      transition("reset", _first(steps))
    ];
    return {
      ...config,
      [step]: state(...transitions)
    };
  }, {});
  return createMachine(
    initialStep || Object.keys(flowConfig)[0],
    flowConfig,
    (context) => context ? { ...values, ...context } : values
  );
};
var mapArgsToFlowTransition = (value, transitionBuilder) => {
  const [stepName, ...guardsOrReducers] = typeof value === "string" ? [value] : value;
  return transitionBuilder(
    stepName,
    ...guardsOrReducers
  );
};
var createFlowMachine = (stepConfig, values, initialStep) => {
  const steps = Object.keys(stepConfig);
  const machineConfig = steps.map((stepName) => {
    const step = createStep(stepName);
    let { next: nextTransitions, previous: previousTransitions } = stepConfig[stepName];
    if (nextTransitions) {
      if (typeof nextTransitions === "string") {
        nextTransitions = [nextTransitions];
      }
      step[stepName].push(
        ...nextTransitions.map(
          (value) => mapArgsToFlowTransition(value, nextStep)
        )
      );
    } else if (nextTransitions !== false && stepName !== _last(steps)) {
      step[stepName].push(nextStep(steps[steps.indexOf(stepName) + 1]));
    }
    if (previousTransitions) {
      if (typeof previousTransitions === "string") {
        previousTransitions = [previousTransitions];
      }
      step[stepName].push(
        ...previousTransitions.map(
          (value) => mapArgsToFlowTransition(value, previousStep)
        )
      );
    } else if (previousTransitions !== false && stepName !== _first(steps)) {
      step[stepName].push(previousStep(steps[steps.indexOf(stepName) - 1]));
    }
    return step;
  });
  return createMachineFromConfig(machineConfig, values, initialStep);
};
function wrapMockStateListener(listener) {
  return (_currentState, event) => {
    listener?.(event.values);
  };
}
function machineReducer(machineState, event) {
  switch (event.type) {
    case "UPDATE":
      return {
        ...machineState,
        currentState: event.nextState,
        context: event.nextContext
      };
    default:
      return machineState;
  }
}
var useMockFlowService = (values, onNextStep, onPreviousStep, onUpdateValues) => {
  const [{ currentState, context }, dispatch] = useReducer(
    (machineState, event) => machineReducer(machineState, event),
    {
      currentState: "ready",
      context: values
    }
  );
  const machineConfig = createMachine(
    {
      ready: state(
        transition(
          "next",
          "finished",
          reducer(),
          action(wrapMockStateListener(onNextStep))
        ),
        transition(
          "previous",
          "finished",
          action(wrapMockStateListener(onPreviousStep))
        ),
        transition(
          "updateValues",
          "ready",
          reducer(),
          action(wrapMockStateListener(onUpdateValues))
        )
      ),
      finished: state()
    },
    (machineContext) => machineContext ? { ...values, ...context } : values
  );
  const flowService = interpret(
    // @ts-expect-error Type '{}' is not comparable to type 'Values'
    machineConfig,
    (service) => {
      dispatch({
        type: "UPDATE",
        nextState: service.machine.current,
        nextContext: service.context
      });
    },
    values
  );
  return { currentState, context, flowService };
};
var useMachine = (machineConfig, basePath) => {
  const navigate = useNavigate2();
  const location = useLocation2();
  const machine = useRef(machineConfig);
  const flow = useRef(
    interpretMachine(machine.current, machine.current.context)
  );
  const [{ currentState, context }, dispatch] = useReducer(
    (machineState, event) => machineReducer(machineState, event),
    {
      currentState: flow.current.machine.current,
      context: flow.current.context
    }
  );
  useEffect(() => {
    if (!_deepEqual(
      Object.keys(machine.current.states),
      Object.keys(machineConfig.states)
    )) {
      machine.current = machineConfig;
      const newFlow = interpretMachine(machineConfig, flow.current.context);
      flow.current = newFlow;
    }
  }, [machineConfig]);
  useEffect(() => {
    const step = getStepNameFromPath(location.pathname, basePath);
    if (step && step !== currentState) {
      navigate(`${basePath}/${currentState}`);
    }
  }, [currentState, basePath, navigate, location.pathname]);
  function interpretMachine(newMachine, values) {
    try {
      return interpret(
        newMachine,
        (service) => {
          if (flow.current.machine.current !== service.machine.current) {
            flow.current.machine = createMachine(
              service.machine.current,
              machine.current.states,
              () => service.context
            );
          }
          dispatch({
            type: "UPDATE",
            nextState: service.machine.current,
            nextContext: service.context
          });
        },
        values || {}
      );
    } catch (error) {
      if (error instanceof Error && error.name === "TypeError" && error.message.includes("enter")) {
        throw new Error(
          `Current step (${machineConfig.current}) is unknown to this flow. Please make sure the missing step is included as one of your configured steps: [${Object.keys(machineConfig.states).join(",")}]`
        );
      }
      throw error;
    }
  }
  return [currentState, context, flow.current];
};
var createStepConfig = ({
  children
}) => {
  const routesElement = React.Children.toArray(children).find((child) => {
    const type = child?.type;
    const typeName = typeof type === "function" && type.name || "";
    return type === Routes || typeName === "Routes" || typeof type === "function" && type.toString().includes("function Routes");
  });
  if (!routesElement) {
    throw new Error(
      "FlowContextProvider must be used with a <Routes> element as a direct child"
    );
  }
  return React.Children.toArray(routesElement.props.children).reduce((result, routeElement) => {
    if (!React.isValidElement(routeElement)) return result;
    const routeType = routeElement.type;
    const routeTypeName = typeof routeType === "function" && routeType.name || "";
    const isRoute = routeType === Route || routeTypeName === "Route";
    if (!isRoute) return result;
    const routeProps = routeElement.props;
    const maybeElement = routeProps.element ?? (routeProps.children ? React.Children.only(routeProps.children) : void 0);
    if (!React.isValidElement(maybeElement)) return result;
    const stepWrapperElement = maybeElement;
    if (stepWrapperElement.type !== StepWrapper) {
      throw new Error(
        "All Routes used with FlowContextProvider must have StepWrapper as a direct and only child"
      );
    }
    const { name, next, previous } = stepWrapperElement.props;
    result[name] = {
      next,
      previous
    };
    return result;
  }, {});
};

// src/Redirect.tsx
import { parse, stringify } from "query-string";
import { Navigate, useLocation as useLocation3 } from "react-router-dom-v6";
import { jsx } from "react/jsx-runtime";
var Redirect = ({
  to,
  preserveQueryParams = true,
  ...rest
}) => {
  let { search } = useLocation3();
  if (preserveQueryParams) {
    if (typeof to === "string") {
      if (to.includes("?")) {
        const [pathname, presetParams] = to.split("?");
        to = { pathname };
        search = stringify(
          { ...parse(search), ...parse(presetParams) },
          { sort: false }
        );
      } else {
        to = {
          pathname: to
        };
      }
    }
    to = { ...to, search };
  }
  return /* @__PURE__ */ jsx(Navigate, { to, replace: true, ...rest });
};

// src/test-helpers/flows/IsolatedStep.tsx
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
function MockFlowContextProvider({
  children,
  onNextStep,
  onPreviousStep,
  onUpdateValues,
  initialValues = {}
}) {
  const {
    currentState: currentStep,
    context: values,
    flowService: flow
  } = useMockFlowService(
    initialValues,
    onNextStep,
    onPreviousStep,
    onUpdateValues
  );
  const value = {
    basePath: "",
    currentStep,
    flow,
    // eslint-disable-line @typescript-eslint/no-explicit-any
    steps: ["ready", "finished"],
    returnToPath: "/",
    values
  };
  const routeElement = React2.Children.only(
    children.props.children
  );
  const stepWrapperElement = React2.Children.only(
    routeElement.props.element
  );
  return /* @__PURE__ */ jsxs(FlowContext.Provider, { value, children: [
    children,
    /* @__PURE__ */ jsx2(Routes2, { children: /* @__PURE__ */ jsx2(
      Route2,
      {
        path: "/",
        element: /* @__PURE__ */ jsx2(Redirect, { to: `/${stepWrapperElement.props.name}` })
      }
    ) })
  ] });
}
function IsolatedStep({
  children,
  component: Component,
  onUpdateValues,
  onNextStep,
  onPreviousStep,
  initialValues = {}
}) {
  return /* @__PURE__ */ jsx2(
    MockFlowContextProvider,
    {
      onUpdateValues,
      onNextStep,
      onPreviousStep,
      initialValues,
      children: /* @__PURE__ */ jsx2(Routes2, { children: /* @__PURE__ */ jsx2(
        Route2,
        {
          path: "/ready",
          element: /* @__PURE__ */ jsx2(StepWrapper, { name: "ready", children: (stepProps) => {
            if (!Component) {
              if (typeof children === "function") {
                return children(stepProps);
              }
              return React2.cloneElement(children, {
                ...stepProps
              });
            }
            return /* @__PURE__ */ jsx2(Component, { ...stepProps });
          } })
        }
      ) })
    }
  );
}

// src/test-helpers/flows/IsolatedStepCompat.tsx
import React3 from "react";
import { Route as Route3, Routes as Routes3 } from "react-router-dom-v5-compat";

// src/flows/components/StepWrapperCompat.tsx
import {
  useLocation as useLocation4,
  useMatch as useMatch2,
  useNavigate as useNavigate3
} from "react-router-dom-v5-compat";
function StepWrapperCompat({
  children
}) {
  const navigate = useNavigate3();
  const location = useLocation4();
  const match = useMatch2(location.pathname);
  const state3 = useFlowState();
  const stepProps = {
    navigate,
    location,
    match,
    ...state3
  };
  return typeof children === "function" ? children(stepProps) : children;
}

// src/RedirectCompat.tsx
import { parse as parse2, stringify as stringify2 } from "query-string";
import {
  Navigate as Navigate2,
  useLocation as useLocation5
} from "react-router-dom-v5-compat";
import { jsx as jsx3 } from "react/jsx-runtime";
var RedirectCompat = ({
  to,
  preserveQueryParams = true,
  ...rest
}) => {
  let { search } = useLocation5();
  if (preserveQueryParams) {
    if (typeof to === "string") {
      if (to.includes("?")) {
        const [pathname, presetParams] = to.split("?");
        to = { pathname };
        search = stringify2(
          { ...parse2(search), ...parse2(presetParams) },
          { sort: false }
        );
      } else {
        to = {
          pathname: to
        };
      }
    }
    to = { ...to, search };
  }
  return /* @__PURE__ */ jsx3(Navigate2, { to, replace: true, ...rest });
};

// src/test-helpers/flows/IsolatedStepCompat.tsx
import { jsx as jsx4, jsxs as jsxs2 } from "react/jsx-runtime";
function MockFlowContextProvider2({
  children,
  onNextStep,
  onPreviousStep,
  onUpdateValues,
  initialValues = {}
}) {
  const {
    currentState: currentStep,
    context: values,
    flowService: flow
  } = useMockFlowService(
    initialValues,
    onNextStep,
    onPreviousStep,
    onUpdateValues
  );
  const value = {
    basePath: "",
    currentStep,
    flow,
    // eslint-disable-line @typescript-eslint/no-explicit-any
    steps: ["ready", "finished"],
    returnToPath: "/",
    values
  };
  const routeElement = React3.Children.only(
    children.props.children
  );
  const stepWrapperElement = React3.Children.only(
    routeElement.props.element
  );
  return /* @__PURE__ */ jsxs2(FlowContext.Provider, { value, children: [
    children,
    /* @__PURE__ */ jsx4(Routes3, { children: /* @__PURE__ */ jsx4(
      Route3,
      {
        path: "/",
        element: /* @__PURE__ */ jsx4(RedirectCompat, { to: `/${stepWrapperElement.props.name}` })
      }
    ) })
  ] });
}
function IsolatedStepCompat({
  children,
  component: Component,
  onUpdateValues,
  onNextStep,
  onPreviousStep,
  initialValues = {}
}) {
  return /* @__PURE__ */ jsx4(
    MockFlowContextProvider2,
    {
      onUpdateValues,
      onNextStep,
      onPreviousStep,
      initialValues,
      children: /* @__PURE__ */ jsx4(Routes3, { children: /* @__PURE__ */ jsx4(
        Route3,
        {
          path: "/ready",
          element: /* @__PURE__ */ jsx4(StepWrapperCompat, { name: "ready", children: (stepProps) => {
            if (!Component) {
              if (typeof children === "function") {
                return children(stepProps);
              }
              return React3.cloneElement(children, {
                ...stepProps
              });
            }
            return /* @__PURE__ */ jsx4(Component, { ...stepProps });
          } })
        }
      ) })
    }
  );
}

// src/api.tsx
import React5 from "react";
import DefaultAxios, { isCancel } from "axios";
import _camelCase from "lodash/camelCase";
import _isObject from "lodash/isObject";
import _snakeCase from "lodash/snakeCase";

// src/csrf.ts
var getCsrfToken = () => {
  const csrfTokenMeta = document.querySelector(
    'meta[name="csrf-token"]'
  );
  if (csrfTokenMeta) {
    return csrfTokenMeta.content;
  }
  return null;
};

// src/use-deep-memo.ts
import React4 from "react";
import _isEqual from "lodash/isEqual";
function useDeepMemo(memoFn, key) {
  const ref = React4.useRef();
  if (!ref.current || !_isEqual(key, ref.current.key)) {
    ref.current = { key, value: memoFn() };
  }
  return ref.current.value;
}

// src/api.tsx
import { jsx as jsx5 } from "react/jsx-runtime";
function convertKeys(data, converter) {
  if (Array.isArray(data)) {
    return data.map((v) => convertKeys(v, converter));
  }
  if (_isObject(data)) {
    return Object.keys(data).reduce(
      (result, key) => ({
        ...result,
        [converter(key)]: convertKeys(data[key], converter)
      }),
      {}
    );
  }
  return data;
}
var camelizeKeys = (data) => data ? convertKeys(data, (k) => _camelCase(k)) : null;
var snakeizeKeys = (data) => data ? convertKeys(data, (k) => _snakeCase(k)) : null;
var SERVICE_UNAVAILABLE_ERROR = {
  messages: { base: ["Looks like something went wrong"] },
  status: 503
};
var parseableHttpStatuses = [200, 201, 422, 401];
async function handleResponse(response, transform) {
  const { status } = response;
  let body;
  if ("cancelled" in response) {
    return { data: void 0, meta: void 0, errors: void 0 };
  }
  if (status === 204) {
    return { data: {} };
  }
  if (status === 404) {
    return {
      errors: {
        messages: { base: ["Looks like something went wrong"] },
        status
      }
    };
  }
  if (status === 503) {
    return { errors: SERVICE_UNAVAILABLE_ERROR };
  }
  if (!parseableHttpStatuses.includes(status)) {
    return {
      errors: {
        messages: { base: ["Looks like something went wrong"] },
        status
      }
    };
  }
  if (status === 422) {
    body = await transform(response);
    body = {
      data: body.data,
      errors: { messages: body.errors, status },
      meta: body.meta
    };
  } else {
    body = await transform(response);
  }
  return camelizeKeys(body);
}
function isNotFound(apiError) {
  if (apiError) {
    return apiError.status === 404;
  }
  return false;
}
function isServiceUnavailable(apiError) {
  if (apiError) {
    return apiError.status === SERVICE_UNAVAILABLE_ERROR.status;
  }
  return false;
}
var defaultAxios = DefaultAxios.create({
  headers: {
    "X-Requested-With": "XMLHttpRequest",
    "Content-Type": "application/json"
  },
  withCredentials: true
});
defaultAxios.interceptors.request.use((config) => {
  config.headers["X-CSRF-Token"] = getCsrfToken();
  return config;
});
var DataFetchContext = React5.createContext(
  null
);
var DataFetchProvider = ({ client = defaultAxios, children }) => {
  return (
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    /* @__PURE__ */ jsx5(DataFetchContext.Provider, { value: { client }, children })
  );
};
function reducer2(state3, [type, payload]) {
  switch (type) {
    case 0 /* START */:
      return {
        ...state3,
        loading: true,
        errors: null,
        networkError: null
      };
    case 1 /* SUCCESS */:
      return {
        ...state3,
        loading: false,
        ...payload
      };
    case 2 /* FAIL */:
      return {
        ...state3,
        loading: false,
        networkError: payload
      };
    case 3 /* CANCEL */:
      return {
        ...state3,
        loading: false
      };
    default:
      return state3;
  }
}
async function runAxiosClient(dispatch, isCurrentRef, client, config) {
  if (!config.url) {
    throw new Error("url required!");
  }
  dispatch([0 /* START */]);
  let response;
  try {
    response = await client(config);
  } catch (error) {
    if (isCancel(error)) {
      response = {
        cancelled: true,
        data: null,
        status: 0,
        statusText: "Cancelled",
        headers: {},
        config
      };
    } else if (error.response) {
      response = error.response;
    } else {
      if (isCurrentRef.current) {
        dispatch([2 /* FAIL */, error]);
      }
      throw error;
    }
  }
  const parsedData = await handleResponse(response, ({ data }) => Promise.resolve(data || {}));
  if (isCurrentRef.current) {
    if ("cancelled" in response) {
      dispatch([3 /* CANCEL */]);
    } else {
      dispatch([1 /* SUCCESS */, parsedData]);
    }
  }
  return parsedData;
}
function useQuery(url, options = { qs: null, skip: false }) {
  const context = React5.useContext(DataFetchContext);
  if (!context) {
    throw new Error("Must be under DataFetchProvider");
  }
  const { client } = context;
  const { qs: _qs, skip } = options;
  const qs4 = useDeepMemo(() => _qs, _qs);
  const isCurrentRef = React5.useRef(true);
  const [state3, dispatch] = React5.useReducer(reducer2, {
    loading: !skip,
    data: null,
    meta: null,
    errors: null,
    networkError: null
  });
  const cancelTokenSourceRef = React5.useRef();
  const cancel = React5.useCallback(
    (message) => cancelTokenSourceRef.current?.cancel(message),
    [cancelTokenSourceRef]
  );
  const query = React5.useCallback(
    (overrides = {}) => {
      cancelTokenSourceRef.current = DefaultAxios.CancelToken.source();
      return runAxiosClient(dispatch, isCurrentRef, client, {
        url,
        params: snakeizeKeys(overrides.qs || qs4),
        cancelToken: cancelTokenSourceRef.current.token
      });
    },
    [url, qs4, client, cancelTokenSourceRef]
  );
  React5.useEffect(() => {
    isCurrentRef.current = true;
    if (!skip) {
      void query();
    }
    return () => {
      isCurrentRef.current = false;
      cancel("Request was automatically cancelled");
    };
  }, [query, skip, cancel]);
  return { ...state3, refetch: query, cancel };
}
function useMutation(url, options = { qs: null, method: "post" }) {
  const context = React5.useContext(DataFetchContext);
  if (!context) {
    throw new Error("Must be under DataFetchProvider");
  }
  const { client } = context;
  const { qs: _qs, method } = options;
  const isCurrentRef = React5.useRef(true);
  const qs4 = useDeepMemo(() => _qs, _qs);
  const [state3, dispatch] = React5.useReducer(reducer2, {
    loading: false,
    data: null,
    meta: null,
    errors: null,
    networkError: null
  });
  const cancelTokenSourceRef = React5.useRef();
  const cancel = React5.useCallback(
    (message) => cancelTokenSourceRef.current?.cancel(message),
    [cancelTokenSourceRef]
  );
  const mutate = React5.useCallback(
    (data) => {
      cancelTokenSourceRef.current = DefaultAxios.CancelToken.source();
      return runAxiosClient(dispatch, isCurrentRef, client, {
        url,
        method,
        params: snakeizeKeys(qs4),
        data: snakeizeKeys(data),
        cancelToken: cancelTokenSourceRef.current.token
      });
    },
    [url, qs4, method, client, cancelTokenSourceRef]
  );
  React5.useEffect(() => {
    return () => {
      isCurrentRef.current = false;
      cancel("Request was automatically cancelled");
    };
  }, [cancel]);
  return [mutate, { ...state3, cancel }];
}

// src/dates.ts
import { format as _formatDate, parseISO as parseDate } from "date-fns";
var DateFormat = /* @__PURE__ */ ((DateFormat2) => {
  DateFormat2["iso8601"] = "yyyy-MM-dd";
  DateFormat2["longOrdinal"] = "MMMM d, yyyy";
  DateFormat2["longOrdinalShortMonth"] = "MMM d, yyyy";
  DateFormat2["longOrdinalSlashed"] = "MM/dd/yyyy";
  DateFormat2["longOrdinalSlashedNonPadded"] = "M/d/yyyy";
  DateFormat2["longOrdinalWithDay"] = "EEEE, MMMM dd, yyyy";
  DateFormat2["longOrdinalWithTimeAndZone"] = "MMMM dd, yyyy h:mma xxxxx";
  DateFormat2["month"] = "MMMM";
  DateFormat2["shortOrdinal"] = "MMMM d";
  DateFormat2["shortOrdinalShortMonth"] = "MMM d";
  return DateFormat2;
})(DateFormat || {});
var formatDate = (value, format = "longOrdinal") => {
  if (typeof value === "string") {
    value = parseDate(value);
  }
  return _formatDate(value, DateFormat[format]);
};

// src/datadog-initializer.ts
import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
var DataDogInitializer = class {
  constructor(config) {
    __publicField(this, "config");
    __publicField(this, "datadogLogs");
    __publicField(this, "datadogRum");
    this.config = config;
    this.datadogLogs = datadogLogs;
    this.datadogRum = datadogRum;
  }
  initialize() {
    if (this.config?.isRumEnabled) {
      datadogRum.init({
        applicationId: this.config.applicationId,
        clientToken: this.config.clientToken,
        site: this.config.site,
        service: this.config.service,
        env: this.config.env || "production",
        sessionSampleRate: this.config.sampleRate ?? 100,
        sessionReplaySampleRate: this.config.replaySampleRate ?? 0,
        trackResources: true,
        trackLongTasks: true,
        trackUserInteractions: true,
        defaultPrivacyLevel: "mask-user-input",
        allowedTracingUrls: this.config.allowedTracingUrls
      });
      datadogRum.startSessionReplayRecording();
    }
    if (this.config?.isLoggingEnabled) {
      datadogLogs.init({
        clientToken: this.config.clientToken,
        site: "datadoghq.com",
        forwardErrorsToLogs: true,
        sessionSampleRate: this.config.logSampleRate ?? 100,
        service: this.config.service,
        env: this.config.env
      });
    }
  }
};

// src/stimulus-app-bootstrapper.ts
import { Application } from "@hotwired/stimulus";

// src/sentry-initializer.ts
import { Replay, configureScope, init } from "@sentry/browser";
import { BrowserTracing } from "@sentry/tracing";
var SentryInitializer = class {
  constructor(config) {
    __publicField(this, "config");
    this.config = config;
  }
  initialize() {
    const integrations = [new BrowserTracing()];
    if (this.config.isReplayEnabled) {
      integrations.push(
        // @ts-expect-error production types from Sentry are mismatched
        new Replay({
          blockAllMedia: false
        })
      );
    }
    init({
      dsn: this.config.sentryApiToken,
      environment: this.config.sentryEnvironment,
      beforeSend: this.config.beforeSend,
      beforeBreadcrumb: this.excludeMiniProfiler.bind(this),
      release: this.config.sentryRelease,
      allowUrls: this.config.sentryWhitelistUrls,
      denyUrls: this.config.sentryDisallowListUrls ?? [],
      integrations,
      tracesSampleRate: this.config.sentryTracesSampleRate ?? 0.2,
      replaysSessionSampleRate: this.config.sentrySessionReplaySampleRate ?? 0,
      replaysOnErrorSampleRate: this.config.sentryErrorReplaySampleRate ?? 0.25,
      tracePropagationTargets: this.config.sentryTracePropagationTargets,
      ignoreErrors: [
        "TypeError: Load failed",
        "TypeError: Failed to fetch",
        "TypeError: NetworkError when attempting to fetch resource.",
        "AxiosError: Network Error"
      ]
    });
    configureScope((scope) => {
      const {
        plan_manager_id: planManagerId,
        id,
        retail_user_id: retailUserId
      } = this.config.user;
      if (planManagerId) {
        scope.setUser({ plan_manager_id: planManagerId });
      } else {
        scope.setUser({ id, retail_user_id: retailUserId });
      }
    });
  }
  excludeMiniProfiler(breadcrumb) {
    return breadcrumb.category === "xhr" && breadcrumb.data && breadcrumb.data.url === "/mini-profiler-resources/results" ? null : breadcrumb;
  }
};

// src/stimulus-app-bootstrapper.ts
var StimulusAppBootstrapper = class {
  constructor(definitions, sentryAppConfig, dataDogConfig) {
    __publicField(this, "sentryInitializer");
    __publicField(this, "sentryAppConfig");
    __publicField(this, "dataDogConfig");
    __publicField(this, "dataDogInitializer");
    __publicField(this, "definitions");
    this.definitions = definitions;
    this.sentryAppConfig = sentryAppConfig;
    this.dataDogConfig = dataDogConfig;
    this.sentryInitializer = new SentryInitializer(this.sentryAppConfig);
    this.dataDogInitializer = this.dataDogConfig ? new DataDogInitializer(this.dataDogConfig) : void 0;
  }
  initialize() {
    const application = Application.start();
    if (this.sentryAppConfig.sentryApiToken) {
      this.sentryInitializer.initialize();
    }
    if (this.dataDogInitializer) {
      this.dataDogInitializer.initialize();
    }
    return this.loadApplication(application);
  }
  async loadApplication(application) {
    await this.domReady();
    application.load(this.definitions);
    return application;
  }
  domReady() {
    return new Promise((resolve) => {
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => resolve());
      } else {
        resolve();
      }
    });
  }
};

// src/money.ts
import numeral from "numeral";
var MoneyFormat = /* @__PURE__ */ ((MoneyFormat2) => {
  MoneyFormat2["credit"] = "+$0,0.00";
  MoneyFormat2["debit"] = "-$0,0.00";
  MoneyFormat2["default"] = "$0,0.00";
  MoneyFormat2["creditOptional"] = "+$0,0[.]00";
  MoneyFormat2["debitOptional"] = "-$0,0[.]00";
  MoneyFormat2["defaultOptional"] = "$0,0[.]00";
  MoneyFormat2["creditDollar"] = "+$0,0";
  MoneyFormat2["debitDollar"] = "-$0,0";
  MoneyFormat2["defaultDollar"] = "$0,0";
  return MoneyFormat2;
})(MoneyFormat || {});
var MoneyValue = class {
  constructor(cents) {
    __publicField(this, "cents");
    this.cents = Number(cents.toFixed(0));
  }
  get dollars() {
    return this.cents / 100;
  }
};
var toMoney = (cents) => {
  return new MoneyValue(cents);
};
var toCentsFromString = (input) => {
  const roundedAndScaledInput = Math.round(parseFloat(input) * 100);
  return roundedAndScaledInput * 1e4 / 1e4;
};
var formatMoney = (money, format = "default") => {
  let { dollars } = money;
  if (/^debit/.exec(format)) {
    dollars = -Math.abs(dollars);
  }
  return numeral(dollars).format(MoneyFormat[format]);
};

// src/breakpoints.ts
import { useEffect as useEffect2, useMemo, useState } from "react";
var queries = {
  atLeastSmall: `(min-width: 500px)`,
  atLeastMedium: `(min-width: 700px)`,
  atLeastLarge: `(min-width: 964px)`,
  atLeastExtraLarge: `(min-width: 1224px)`
};
function useMatchMedia(breakpoint) {
  const query = queries[breakpoint];
  const mediaQueryList = useMemo(() => window.matchMedia(query), [query]);
  const [isMatch, setMatch] = useState(mediaQueryList.matches);
  useEffect2(() => {
    const handleMatchChange = ({ matches }) => setMatch(matches);
    mediaQueryList.addListener(handleMatchChange);
    return () => {
      mediaQueryList.removeListener(handleMatchChange);
    };
  }, [mediaQueryList, query]);
  return isMatch;
}

// src/analytics.ts
import _camelCase2 from "lodash/camelCase";
import _upperFirst from "lodash/upperFirst";

// src/keyboard-events.ts
import { Key } from "w3c-keys";
var isEnterKeydown = (event) => {
  return event.key === Key.Enter;
};
var isSpacebarSelectableElement = (element) => {
  return element.tagName.toLowerCase() === "button" || element.getAttribute("role") === "button";
};
var isSpaceKeydown = (event) => {
  return event.key === Key.Space;
};
var isEligibleSpaceKeydown = (element, event) => {
  return isSpaceKeydown(event) && isSpacebarSelectableElement(element);
};
var isKeyboardSelection = (element, event) => {
  return isEnterKeydown(event) || isEligibleSpaceKeydown(element, event);
};

// src/path-helpers.ts
import { useState as useState2 } from "react";
import * as qs from "query-string";
import { useLocation as useLocation6 } from "react-router-dom-v6";
var ALLOWED_BMT_DOMAINS = [
  /\.bettermentforadvisors.com$/,
  /\.betterment\.com$/,
  /\.betterment\.io$/,
  /\.betterment\.qa$/,
  /^bettermentforadvisors\.com$/,
  /^betterment\.com$/,
  /^betterment\.io$/,
  /^betterment\.qa$/,
  /test$/,
  /^localhost$/
];
var sanitizeIds = (subject) => {
  const NUMERIC_ID_REGEX = /\/(?!401k)[0-9]+/g;
  const UUID_REGEX = /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/g;
  return subject.replace(UUID_REGEX, "/_").replace(NUMERIC_ID_REGEX, "/_");
};
var sanitizedPath = () => sanitizeIds(window.location.pathname);
var sanitizedTokenQuery = () => {
  const { token, ...rest } = qs.parse(window.location.search);
  if (token) {
    return qs.stringify({ token: "_", ...rest });
  }
  return window.location.search;
};
var sanitizedUrl = () => {
  const query = sanitizedTokenQuery();
  return `${window.location.origin}${sanitizeIds(window.location.pathname)}${query ? `?${query}` : ""}`;
};
var removeTrailingSlashes = (path) => path.endsWith("/") ? path.replace(/\/+$/, "") : path;
function isBmtAllowedURL(url) {
  try {
    const address = new URL(url);
    const allowedProtocols = ["http:", "https:"];
    const hasAllowedProtocol = allowedProtocols.find(
      (protocol) => address.protocol === protocol
    );
    if (!hasAllowedProtocol) {
      return false;
    }
    return ALLOWED_BMT_DOMAINS.find(
      (domain) => RegExp(domain).exec(address.hostname)
    );
  } catch (e) {
    return true;
  }
}
function sanitizeUrlParam(url, defaultReturn = "") {
  if (!url) {
    return defaultReturn;
  }
  const DENIED_NONPRITABLE_CHARS_REGEX = /[^\x20-\x7EÀ-ž]/gim;
  const DENIED_CHARACTERS = /[\<\>\"\'\%\;\(\)]/g;
  const cleanUrl = decodeURI(url).replace(DENIED_NONPRITABLE_CHARS_REGEX, "").replace(DENIED_CHARACTERS, "").trim();
  if (isBmtAllowedURL(cleanUrl)) {
    return cleanUrl;
  }
  return defaultReturn;
}
function useQueryParams(options) {
  const { search } = useLocation6();
  const params = camelizeKeys(qs.parse(search)) || {};
  return useState2(() => {
    if (options?.parseArrays) {
      return params;
    }
    return Object.keys(params).reduce((result, key) => {
      const val = params[key];
      return {
        ...result,
        [key]: Array.isArray(val) ? val[0] : val
      };
    }, {});
  })[0];
}
var useReturnToPath = (defaultPath = "") => {
  const { returnTo } = useQueryParams();
  return sanitizeUrlParam(returnTo, defaultPath);
};
var useExitToPath = (defaultPath = "") => {
  const { exitTo } = useQueryParams();
  return sanitizeUrlParam(exitTo, defaultPath);
};

// src/segment-analytics.ts
var segmentAnalytics = () => {
  return window.analytics;
};

// src/analytics.ts
var INTERACTION_LISTENER_SELECTOR = '[data-track-event="ElementClicked"], [data-track-event="AppStoreSelected"]';
var HOVER_LISTENER_SELECTOR = '[data-track-event="ElementHovered"]';
var VIEWED_LISTENER_SELECTOR = '[data-track-event="ElementViewed"]';
var HOVER_LISTENER_REGISTERED_DATA_ATTR = "data-hover-listener-registered";
var VIEW_LISTENER_REGISTERED_DATA_ATTR = "data-view-listener-registered";
var INTERACTION_LISTENER_REGISTERED_DATA_ATTR = "data-interaction-listener-registered";
var LISTENER_REGISTERED_ATTR_VALUE = "true";
var PAGE_LOAD_WAIT_INTERVAL = 3e3;
var applicationName;
var analyticsReadyResolver;
var analyticsReady = new Promise((resolve) => {
  analyticsReadyResolver = resolve;
});
var getClient = () => {
  const ua = window.navigator.userAgent;
  if (/Android.*Betterment/.test(ua)) {
    return "AndroidWebView";
  }
  if (/Android/.test(ua)) {
    return "AndroidMobileWeb";
  }
  if (/(iPhone|iPod|iPad).*Safari/.test(ua)) {
    return "iOSMobileWeb";
  }
  if (/(iPhone|iPod|iPad)/.test(ua)) {
    return "iOSWebView";
  }
  return "Web";
};
var elementAttributes = (element) => {
  const dataName = element.getAttribute("data-track-name");
  const textContent = element.textContent || void 0;
  return {
    name: dataName || _upperFirst(_camelCase2(textContent)),
    flow: element.getAttribute("data-track-flow"),
    module: element.getAttribute("data-track-module"),
    location: element.getAttribute("data-track-location")
  };
};
var commonAttributes = () => ({
  application: applicationName,
  client: getClient(),
  path: sanitizedPath(),
  search: sanitizedTokenQuery(),
  url: sanitizedUrl()
});
var track = (eventName, properties, callback) => {
  const eventProperties = { ...commonAttributes(), ...properties };
  void analyticsReady.then(() => {
    segmentAnalytics().track(eventName, eventProperties, callback);
  });
};
var trackInteraction = (element, interaction) => {
  const eventName = element.getAttribute("data-track-event");
  if (eventName === "ElementClicked" || eventName === "AppStoreSelected") {
    const eventProperties = { ...elementAttributes(element), interaction };
    track(eventName, eventProperties);
  }
};
var trackKeydown = (element, event) => {
  if (isKeyboardSelection(element, event)) {
    trackInteraction(element, "keydown");
  }
};
var trackHoverOrView = (element) => {
  const eventName = element.getAttribute("data-track-event");
  if (eventName === "ElementHovered" || eventName === "ElementViewed") {
    const eventProperties = { ...elementAttributes(element) };
    track(eventName, eventProperties);
  }
};
var isNotBot = () => {
  return window._DATADOG_SYNTHETICS_BROWSER === void 0;
};
var attachInteractionListeners = () => {
  const elements = [].slice.call(
    document.querySelectorAll(INTERACTION_LISTENER_SELECTOR)
  );
  elements.forEach((element) => {
    if (element.getAttribute(INTERACTION_LISTENER_REGISTERED_DATA_ATTR) !== LISTENER_REGISTERED_ATTR_VALUE) {
      element.addEventListener(
        "click",
        () => trackInteraction(element, "click")
      );
      element.addEventListener(
        "keydown",
        (ev) => trackKeydown(element, ev)
      );
      element.setAttribute(
        INTERACTION_LISTENER_REGISTERED_DATA_ATTR,
        LISTENER_REGISTERED_ATTR_VALUE
      );
    }
  });
};
var attachViewListeners = () => {
  const elements = [].slice.call(
    document.querySelectorAll(VIEWED_LISTENER_SELECTOR)
  );
  elements.forEach((element) => {
    if (element.getAttribute(VIEW_LISTENER_REGISTERED_DATA_ATTR) !== LISTENER_REGISTERED_ATTR_VALUE) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.every((e) => e.isIntersecting)) {
            observer.unobserve(element);
            trackHoverOrView(element);
          }
        },
        { threshold: 1 }
      );
      observer.observe(element);
      element.setAttribute(
        VIEW_LISTENER_REGISTERED_DATA_ATTR,
        LISTENER_REGISTERED_ATTR_VALUE
      );
    }
  });
};
var attachHoverListeners = () => {
  const elements = [].slice.call(
    document.querySelectorAll(HOVER_LISTENER_SELECTOR)
  );
  elements.forEach((element) => {
    if (element.getAttribute(HOVER_LISTENER_REGISTERED_DATA_ATTR) !== LISTENER_REGISTERED_ATTR_VALUE) {
      element.addEventListener("mouseenter", () => trackHoverOrView(element), {
        once: true
      });
      element.setAttribute(
        HOVER_LISTENER_REGISTERED_DATA_ATTR,
        LISTENER_REGISTERED_ATTR_VALUE
      );
    }
  });
};
var attachListeners = () => {
  attachInteractionListeners();
  attachHoverListeners();
  attachViewListeners();
};
var trackPageView = (pageMetadata = {}) => {
  void analyticsReady.then(() => {
    segmentAnalytics().page({ ...commonAttributes(), ...pageMetadata });
  });
};
var setUpNewPage = async () => {
  const turbolinksLoaded = new Promise((resolve) => {
    const loaded = () => {
      document.removeEventListener("turbolinks:load", loaded);
      resolve();
    };
    document.addEventListener("turbolinks:load", loaded);
  });
  const timeoutPassed = new Promise((resolve) => {
    setTimeout(resolve, PAGE_LOAD_WAIT_INTERVAL);
  });
  trackPageView();
  await Promise.race([timeoutPassed, turbolinksLoaded]);
  attachListeners();
};
var applyHistoryMonkeyPatches = () => {
  const pushStateOriginal = window.history.pushState.bind(window.history);
  const replaceStateOriginal = window.history.replaceState.bind(window.history);
  window.history.pushState = function pushOverride(data, title, url) {
    void setUpNewPage();
    return pushStateOriginal.apply(this, [data, title, url]);
  };
  window.history.replaceState = function replaceOverride(data, title, url) {
    void setUpNewPage();
    return replaceStateOriginal.apply(this, [data, title, url]);
  };
};
var mutationObserver = new MutationObserver(() => {
  attachListeners();
});
var startAnalytics = (testTrackVisitorId, application, pageMetadata) => {
  applicationName = application;
  segmentAnalytics().ready(() => {
    if (segmentAnalytics().user().id() !== testTrackVisitorId) {
      segmentAnalytics().identify(testTrackVisitorId);
    }
    if (isNotBot()) {
      analyticsReadyResolver(true);
    }
    trackPageView(pageMetadata);
    applyHistoryMonkeyPatches();
    attachListeners();
    mutationObserver.observe(document, {
      childList: true,
      subtree: true
    });
  });
};
function setTraits(properties) {
  void analyticsReady.then(() => {
    segmentAnalytics().identify(void 0, properties);
  });
}

// src/vendor/segment-snippet.js
function segment_snippet_default(writeKey) {
  var analytics = window.analytics = window.analytics || [];
  if (analytics.initialize) return;
  if (analytics.invoked) {
    if (window.console && console.error) {
      console.error("Segment snippet included twice.");
    }
    return;
  }
  analytics.invoked = true;
  analytics.methods = [
    "trackSubmit",
    "trackClick",
    "trackLink",
    "trackForm",
    "pageview",
    "identify",
    "reset",
    "group",
    "track",
    "ready",
    "alias",
    "debug",
    "page",
    "once",
    "off",
    "on"
  ];
  analytics.factory = function(method) {
    return function() {
      var args = Array.prototype.slice.call(arguments);
      args.unshift(method);
      analytics.push(args);
      return analytics;
    };
  };
  for (var i = 0; i < analytics.methods.length; i++) {
    var key = analytics.methods[i];
    analytics[key] = analytics.factory(key);
  }
  analytics.load = function(key2, options) {
    var script = document.createElement("script");
    script.type = "text/javascript";
    script.async = true;
    script.src = "https://cdn.segment.com/analytics.js/v1/" + key2 + "/analytics.min.js";
    var first = document.getElementsByTagName("script")[0];
    first.parentNode.insertBefore(script, first);
    analytics._loadOptions = options;
  };
  analytics.SNIPPET_VERSION = "4.1.0";
  analytics.load(writeKey);
}

// src/segment-initializer.ts
var segmentInitializer = (writeKey) => {
  segment_snippet_default(writeKey);
};

// src/path-helpers-compat.ts
import { useState as useState3 } from "react";
import * as qs2 from "query-string";
import { useLocation as useLocation7 } from "react-router-dom-v5-compat";
function useQueryParamsCompat(options) {
  const { search } = useLocation7();
  const params = camelizeKeys(qs2.parse(search)) || {};
  return useState3(() => {
    if (options?.parseArrays) {
      return params;
    }
    return Object.keys(params).reduce((result, key) => {
      const val = params[key];
      return {
        ...result,
        [key]: Array.isArray(val) ? val[0] : val
      };
    }, {});
  })[0];
}
var useReturnToPathCompat = (defaultPath = "") => {
  const { returnTo } = useQueryParamsCompat();
  return sanitizeUrlParam(returnTo, defaultPath);
};
var useExitToPathCompat = (defaultPath = "") => {
  const { exitTo } = useQueryParamsCompat();
  return sanitizeUrlParam(exitTo, defaultPath);
};

// src/flows/components/SessionSync.tsx
import { useEffect as useEffect3 } from "react";
import _pick from "lodash/pick";

// src/use-session-storage.ts
import { useCallback as useCallback2, useMemo as useMemo2 } from "react";
import _camelCase3 from "lodash/camelCase";
import invariant2 from "tiny-invariant";
var FILTER_FIELDS = Object.freeze([
  "accountNumber",
  "addGoalCustomImageData",
  "address",
  "address1",
  "address2",
  "answer",
  "cashflowCategory",
  "cashflowSubcategory",
  "city",
  "confirmPassword",
  "customImageData",
  "dateOfBirth",
  "dobYear",
  "dobMonth",
  "documentContent",
  "email",
  "employer",
  "encodedFile",
  "federalTaxIdNumber",
  "firstName",
  "lastName",
  "legalName",
  "memo",
  "nickname",
  "password",
  "performerPassword",
  "question",
  "phone",
  "phoneNumber",
  "proofOfAddress",
  "routingNumber",
  "secondaryDocument",
  "secret",
  "securityAnswer1",
  "securityAnswer2",
  "socialSecurityNumber",
  "ssn",
  "taxId",
  "taxpayerIdentificationNumber",
  "usGovId",
  "userName",
  "zip",
  "zipCode"
]);
var useSessionStorage = (key, initialValues, { ttl = 600 } = {}) => {
  invariant2(
    !Object.keys(initialValues || {}).some(
      (field) => FILTER_FIELDS.includes(_camelCase3(field))
    ),
    `Stored values must not be one of the following: ${FILTER_FIELDS.join(
      ", "
    )}`
  );
  const expiry = Number(/* @__PURE__ */ new Date()) + ttl * 1e3;
  const namespacedKey = `bmt.${key}`;
  if (initialValues && !sessionStorage.getItem(namespacedKey)) {
    sessionStorage.setItem(
      namespacedKey,
      JSON.stringify({ expiry, value: initialValues })
    );
  }
  const storedValues = sessionStorage.getItem(namespacedKey);
  const values = useMemo2(() => {
    if (storedValues) {
      const { expiry: storedExpirey, value } = JSON.parse(storedValues);
      if (Date.now() < storedExpirey) return value;
    }
    return null;
  }, [storedValues]);
  const saveValues = useCallback2(
    (newValues) => {
      sessionStorage.setItem(
        namespacedKey,
        JSON.stringify({
          expiry,
          value: newValues
        })
      );
    },
    [namespacedKey, expiry]
  );
  const clearValues = useCallback2(() => {
    sessionStorage.removeItem(namespacedKey);
  }, [namespacedKey]);
  return { values, saveValues, clearValues };
};

// src/flows/components/SessionSync.tsx
function SessionSync({
  sessionKey,
  safeFields
}) {
  const { saveValues } = useSessionStorage(sessionKey);
  const { flowState } = useFlowState();
  const subset = safeFields ? _pick(flowState.values, safeFields) : flowState.values;
  const safeValues = useDeepMemo(() => subset, subset);
  useEffect3(() => {
    saveValues(safeValues);
  }, [saveValues, safeValues]);
  return null;
}

// src/flows/components/FlowContextProvider.tsx
import { useEffect as useEffect4, useMemo as useMemo3 } from "react";
import {
  NavigationType,
  Route as Route4,
  Routes as Routes4,
  useLocation as useLocation8,
  useNavigationType
} from "react-router-dom-v6";
import { jsx as jsx6, jsxs as jsxs3 } from "react/jsx-runtime";
var LocationSyncer = () => {
  const action3 = useNavigationType();
  const location = useLocation8();
  const { sync, flowState } = useFlowState();
  const step = getStepNameFromPath(location.pathname, flowState.basePath);
  useEffect4(() => {
    if (action3 === NavigationType.Pop && step) {
      sync(step);
    }
  }, [action3, step, sync]);
  return null;
};
function FlowContextProvider({
  basePath = "",
  routePath = basePath,
  children,
  currentStep,
  initialValues = {},
  defaultStep,
  returnToPath: initialReturnToPath
}) {
  const location = useLocation8();
  const stepConfig = useMemo3(
    () => createStepConfig({ children }),
    [children]
  );
  const steps = Object.keys(stepConfig);
  const returnToPath = useReturnToPath(initialReturnToPath);
  basePath = removeTrailingSlashes(basePath);
  defaultStep = defaultStep || steps[0];
  currentStep = currentStep || getStepNameFromPath(location.pathname, basePath) || defaultStep;
  const machineConfig = createFlowMachine(
    stepConfig,
    initialValues,
    currentStep
  );
  const [currentState, values, flow] = useMachine(machineConfig, basePath);
  const value = {
    basePath,
    currentStep: currentState,
    flow,
    steps,
    returnToPath,
    values
  };
  return /* @__PURE__ */ jsxs3(FlowContext.Provider, { value, children: [
    /* @__PURE__ */ jsx6(LocationSyncer, {}),
    children,
    /* @__PURE__ */ jsxs3(Routes4, { children: [
      /* @__PURE__ */ jsx6(
        Route4,
        {
          path: `${routePath}/`,
          element: /* @__PURE__ */ jsx6(Redirect, { to: `${basePath}/${defaultStep}` })
        }
      ),
      basePath && /* @__PURE__ */ jsx6(
        Route4,
        {
          path: routePath,
          element: /* @__PURE__ */ jsx6(Redirect, { to: `${basePath}/${defaultStep}` })
        }
      )
    ] })
  ] });
}

// src/flows/components/FlowContextProviderCompat.tsx
import { useEffect as useEffect6, useMemo as useMemo4 } from "react";
import {
  NavigationType as NavigationType2,
  Route as Route6,
  Routes as Routes6,
  useLocation as useLocation10,
  useNavigationType as useNavigationType2
} from "react-router-dom-v5-compat";

// src/flows/utilities-compat.ts
import React7, { useEffect as useEffect5, useReducer as useReducer2, useRef as useRef2 } from "react";
import _first2 from "lodash/first";
import _deepEqual2 from "lodash/isEqual";
import _last2 from "lodash/last";
import {
  CompatRoute,
  Route as Route5,
  Routes as Routes5,
  useLocation as useLocation9,
  useNavigate as useNavigate4
} from "react-router-dom-v5-compat";
import {
  action as action2,
  createMachine as createMachine2,
  guard as guard2,
  interpret as interpret2,
  reduce as reduce2,
  state as state2,
  transition as transition2
} from "robot3";
var getStepNameFromPathCompat = (pathname, basePath) => {
  if (!basePath.endsWith("/")) basePath = `${basePath}/`;
  if (!pathname.startsWith(basePath)) return "";
  return pathname.substring(basePath.length);
};
var reducer3 = () => reduce2((context, event) => ({
  ...context,
  ...event.values
}));
var nextStep2 = (stepName, ...guardsOrReducers) => {
  return transition2("next", stepName, ...guardsOrReducers, reducer3());
};
var previousStep2 = (stepName, ...guardsOrReducers) => {
  return transition2("previous", stepName, ...guardsOrReducers);
};
var createStep2 = (stepName) => {
  return {
    [stepName]: [transition2("updateValues", stepName, reducer3())]
  };
};
var createMachineFromConfig2 = (stepConfig, values, initialStep) => {
  const steps = stepConfig.map((step) => {
    return Object.keys(step)[0];
  });
  const flowConfig = steps.reduce((config, step, index) => {
    const transitions = [
      ...stepConfig[index][step],
      ...steps.map((s) => transition2(s, s)),
      transition2("reset", _first2(steps))
    ];
    return {
      ...config,
      [step]: state2(...transitions)
    };
  }, {});
  return createMachine2(
    initialStep || Object.keys(flowConfig)[0],
    flowConfig,
    (context) => context ? { ...values, ...context } : values
  );
};
var mapArgsToFlowTransition2 = (value, transitionBuilder) => {
  const [stepName, ...guardsOrReducers] = typeof value === "string" ? [value] : value;
  return transitionBuilder(
    stepName,
    ...guardsOrReducers
  );
};
var createFlowMachineCompat = (stepConfig, values, initialStep) => {
  const steps = Object.keys(stepConfig);
  const machineConfig = steps.map((stepName) => {
    const step = createStep2(stepName);
    let { next: nextTransitions, previous: previousTransitions } = stepConfig[stepName];
    if (nextTransitions) {
      if (typeof nextTransitions === "string") {
        nextTransitions = [nextTransitions];
      }
      step[stepName].push(
        ...nextTransitions.map(
          (value) => mapArgsToFlowTransition2(value, nextStep2)
        )
      );
    } else if (nextTransitions !== false && stepName !== _last2(steps)) {
      step[stepName].push(nextStep2(steps[steps.indexOf(stepName) + 1]));
    }
    if (previousTransitions) {
      if (typeof previousTransitions === "string") {
        previousTransitions = [previousTransitions];
      }
      step[stepName].push(
        ...previousTransitions.map(
          (value) => mapArgsToFlowTransition2(value, previousStep2)
        )
      );
    } else if (previousTransitions !== false && stepName !== _first2(steps)) {
      step[stepName].push(previousStep2(steps[steps.indexOf(stepName) - 1]));
    }
    return step;
  });
  return createMachineFromConfig2(machineConfig, values, initialStep);
};
function machineReducer2(machineState, event) {
  switch (event.type) {
    case "UPDATE":
      return {
        ...machineState,
        currentState: event.nextState,
        context: event.nextContext
      };
    default:
      return machineState;
  }
}
var useMachineCompat = (machineConfig, basePath) => {
  const navigate = useNavigate4();
  const location = useLocation9();
  const machine = useRef2(machineConfig);
  const flow = useRef2(
    interpretMachine(machine.current, machine.current.context)
  );
  const [{ currentState, context }, dispatch] = useReducer2(
    (machineState, event) => machineReducer2(machineState, event),
    {
      currentState: flow.current.machine.current,
      context: flow.current.context
    }
  );
  useEffect5(() => {
    if (!_deepEqual2(
      Object.keys(machine.current.states),
      Object.keys(machineConfig.states)
    )) {
      machine.current = machineConfig;
      const newFlow = interpretMachine(machineConfig, flow.current.context);
      flow.current = newFlow;
    }
  }, [machineConfig]);
  useEffect5(() => {
    const step = getStepNameFromPathCompat(location.pathname, basePath);
    if (step && step !== currentState) {
      navigate(`${basePath}/${currentState}`);
    }
  }, [currentState, basePath, navigate, location.pathname]);
  function interpretMachine(newMachine, values) {
    try {
      return interpret2(
        newMachine,
        (service) => {
          if (flow.current.machine.current !== service.machine.current) {
            flow.current.machine = createMachine2(
              service.machine.current,
              machine.current.states,
              () => service.context
            );
          }
          dispatch({
            type: "UPDATE",
            nextState: service.machine.current,
            nextContext: service.context
          });
        },
        values || {}
      );
    } catch (error) {
      if (error instanceof Error && error.name === "TypeError" && error.message.includes("enter")) {
        throw new Error(
          `Current step (${machineConfig.current}) is unknown to this flow. Please make sure the missing step is included as one of your configured steps: [${Object.keys(machineConfig.states).join(",")}]`
        );
      }
      throw error;
    }
  }
  return [currentState, context, flow.current];
};
var createStepConfigCompat = ({
  children
}) => {
  const switchOrRoutesElement = React7.Children.toArray(children).find((child) => {
    const type = child?.type;
    const typeName = typeof type === "function" && type.name || "";
    return type === Routes5 || typeName === "Routes" || typeof type === "function" && type.toString().includes("function Routes");
  });
  if (!switchOrRoutesElement) {
    throw new Error(
      "FlowContextProvider must be used with a <Switch> or <Routes> element as a direct child"
    );
  }
  return React7.Children.toArray(switchOrRoutesElement.props.children).reduce((result, routeElement) => {
    if (!React7.isValidElement(routeElement)) return result;
    const routeType = routeElement.type;
    const routeTypeName = typeof routeType === "function" && routeType.name || "";
    const isRoute = routeType === Route5 || routeType === CompatRoute || routeTypeName === "Route" || routeTypeName === "CompatRoute";
    if (!isRoute) return result;
    const routeProps = routeElement.props;
    const maybeElement = routeProps.element ?? (routeProps.children ? React7.Children.only(routeProps.children) : void 0);
    if (!React7.isValidElement(maybeElement)) return result;
    const stepWrapperElement = maybeElement;
    if (stepWrapperElement.type !== StepWrapperCompat) {
      throw new Error(
        "All Routes used with FlowContextProvider must have StepWrapperCompat as a direct and only child"
      );
    }
    const { name, next, previous } = stepWrapperElement.props;
    result[name] = {
      next,
      previous
    };
    return result;
  }, {});
};

// src/flows/components/FlowContextProviderCompat.tsx
import { jsx as jsx7, jsxs as jsxs4 } from "react/jsx-runtime";
var LocationSyncer2 = () => {
  const action3 = useNavigationType2();
  const location = useLocation10();
  const { sync, flowState } = useFlowState();
  const step = getStepNameFromPathCompat(location.pathname, flowState.basePath);
  useEffect6(() => {
    if (action3 === NavigationType2.Pop && step) {
      sync(step);
    }
  }, [action3, step, sync]);
  return null;
};
function FlowContextProviderCompat({
  basePath = "",
  routePath = basePath,
  children,
  currentStep,
  initialValues = {},
  defaultStep,
  returnToPath: initialReturnToPath
}) {
  const location = useLocation10();
  const stepConfig = useMemo4(
    () => createStepConfigCompat({ children }),
    [children]
  );
  const steps = Object.keys(stepConfig);
  const returnToPath = useReturnToPathCompat(initialReturnToPath);
  basePath = removeTrailingSlashes(basePath);
  defaultStep = defaultStep || steps[0];
  currentStep = currentStep || getStepNameFromPathCompat(location.pathname, basePath) || defaultStep;
  const machineConfig = createFlowMachineCompat(
    stepConfig,
    initialValues,
    currentStep
  );
  const [currentState, values, flow] = useMachineCompat(machineConfig, basePath);
  const value = {
    basePath,
    currentStep: currentState,
    flow,
    steps,
    returnToPath,
    values
  };
  return /* @__PURE__ */ jsxs4(FlowContext.Provider, { value, children: [
    /* @__PURE__ */ jsx7(LocationSyncer2, {}),
    children,
    /* @__PURE__ */ jsxs4(Routes6, { children: [
      /* @__PURE__ */ jsx7(
        Route6,
        {
          path: `${routePath}/`,
          element: /* @__PURE__ */ jsx7(RedirectCompat, { to: `${basePath}/${defaultStep}` })
        }
      ),
      basePath && /* @__PURE__ */ jsx7(
        Route6,
        {
          path: routePath,
          element: /* @__PURE__ */ jsx7(RedirectCompat, { to: `${basePath}/${defaultStep}` })
        }
      )
    ] })
  ] });
}

// src/flows/components/FlowSessionRoutes.tsx
import { Route as Route7, Routes as Routes7 } from "react-router-dom-v6";
import { v4 as uuidV4 } from "uuid";
import { jsx as jsx8, jsxs as jsxs5 } from "react/jsx-runtime";
var RedirectWithUUID = ({ baseTo }) => {
  return /* @__PURE__ */ jsx8(Redirect, { to: `${baseTo}/${uuidV4()}` });
};
var FlowSessionRoutes = ({ path, routePath = path, children }) => /* @__PURE__ */ jsxs5(Routes7, { children: [
  /* @__PURE__ */ jsx8(Route7, { path: routePath, element: /* @__PURE__ */ jsx8(RedirectWithUUID, { baseTo: path }) }),
  /* @__PURE__ */ jsx8(
    Route7,
    {
      path: `${routePath}/new`,
      element: /* @__PURE__ */ jsx8(RedirectWithUUID, { baseTo: path })
    }
  ),
  /* @__PURE__ */ jsx8(Route7, { path: `${routePath}/:id/*`, element: children })
] });

// src/flows/components/FlowSessionRoutesCompat.tsx
import { Route as Route8, Routes as Routes8 } from "react-router-dom-v5-compat";
import { v4 as uuidV42 } from "uuid";
import { jsx as jsx9, jsxs as jsxs6 } from "react/jsx-runtime";
var RedirectWithUUID2 = ({ baseTo }) => {
  return /* @__PURE__ */ jsx9(RedirectCompat, { to: `${baseTo}/${uuidV42()}` });
};
var FlowSessionRoutesCompat = ({ path, routePath = path, children }) => /* @__PURE__ */ jsxs6(Routes8, { children: [
  /* @__PURE__ */ jsx9(Route8, { path: routePath, element: /* @__PURE__ */ jsx9(RedirectWithUUID2, { baseTo: path }) }),
  /* @__PURE__ */ jsx9(
    Route8,
    {
      path: `${routePath}/new`,
      element: /* @__PURE__ */ jsx9(RedirectWithUUID2, { baseTo: path })
    }
  ),
  /* @__PURE__ */ jsx9(Route8, { path: `${routePath}/:id/*`, element: children })
] });

// src/route.ts
import qs3 from "query-string";
function trimSlashes(path) {
  return path.replace(/^[/]+/, "").replace(/[/]+$/, "");
}
function staticRoute(basePath, components, defaultQuery) {
  const isAbsolute = basePath === "/";
  const path = `/${components.map(trimSlashes).join("/")}`;
  const definition = (queryParams = {}) => qs3.stringifyUrl({
    url: path,
    query: { ...defaultQuery, ...queryParams }
  });
  definition.path = path;
  definition.absolutePath = isAbsolute ? path : `${basePath}${path}`;
  definition.absolute = isAbsolute ? definition : staticRoute("/", [basePath, ...components], defaultQuery);
  return definition;
}
function parameterizedRoute(basePath, components, defaultQuery) {
  const isAbsolute = basePath === "/";
  const path = `/${components.map((c) => typeof c === "string" ? trimSlashes(c) : `:${c.param}`).join("/")}`;
  const routeParamNames = components.filter((c) => typeof c !== "string").map(({ param }) => param);
  const definition = (queryAndRouteParams) => {
    const url = `/${components.map(
      (c) => typeof c === "string" ? trimSlashes(c) : queryAndRouteParams[c.param]
    ).join("/")}`;
    const query = routeParamNames.reduce(
      (remainingParams, routeParamName) => ({
        ...remainingParams,
        [routeParamName]: void 0
      }),
      { ...defaultQuery, ...queryAndRouteParams }
    );
    return qs3.stringifyUrl({ url, query });
  };
  definition.path = path;
  definition.absolutePath = isAbsolute ? path : `${basePath}${path}`;
  definition.absolute = isAbsolute ? definition : parameterizedRoute("/", [basePath, ...components], defaultQuery);
  return definition;
}
function route(basePath, components, defaultQuery = {}) {
  const stringComponents = components.filter(
    (c) => typeof c === "string"
  );
  if (components.toString() === stringComponents.toString()) {
    return staticRoute(basePath, stringComponents, defaultQuery);
  }
  return parameterizedRoute(basePath, components, defaultQuery);
}

// src/use-sorted.ts
import { useState as useState4 } from "react";
function removeDuplicateSorts(sorts) {
  return sorts.reduce(
    (filteredSorts, { attr, direction }) => {
      return [
        ...filteredSorts.filter((o) => o.attr !== attr),
        { attr, direction }
      ];
    },
    []
  );
}
var comparableTypes = ["number", "string", "bigint", "boolean"];
function cmp(a, b) {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}
function defaultCompare(a, b) {
  const aType = a === null ? "null" : typeof a;
  const bType = b === null ? "null" : typeof b;
  const isAComparable = comparableTypes.indexOf(aType) !== -1;
  const isBComparable = comparableTypes.indexOf(bType) !== -1;
  if (isAComparable && !isBComparable) return -1;
  if (!isAComparable && isBComparable) return 1;
  if (isAComparable) {
    if (aType === bType) {
      return cmp(a, b);
    }
    return cmp(aType, bType);
  }
  return cmp(a.toString(), b.toString());
}
function sort(compareFns, activeSorts, items) {
  const sortOrder = activeSorts.slice().reverse();
  const itemsWithIndex = items.map((item, index) => ({ item, index }));
  const sortedItemsWithIndex = itemsWithIndex.sort((a, b) => {
    const compareResult = sortOrder.reduce(
      (result, { attr, direction }) => {
        if (result !== 0) return result;
        const compareFn = compareFns[attr] ?? defaultCompare;
        const compared = compareFn(a.item[attr], b.item[attr]);
        if (compared !== 0) return direction === "asc" ? compared : -compared;
        return result;
      },
      0
    );
    if (compareResult !== 0) return compareResult;
    return a.index - b.index;
  });
  return sortedItemsWithIndex.map(({ item }) => item);
}
function createSortBy(setActiveSortList) {
  return (attr, direction) => {
    function appendSort(activeSorts) {
      return [...activeSorts.filter((o) => o.attr !== attr), { attr, direction }];
    }
    setActiveSortList(appendSort);
  };
}
function createSortState(activeSorts) {
  const sortState = {};
  activeSorts.forEach(({ attr, direction }) => {
    sortState[attr] = direction;
  });
  return sortState;
}
function useSorted(items, { initialSorts = [], compareFunctions = {} } = {}) {
  const [activeSorts, setActiveSortList] = useState4(
    () => removeDuplicateSorts(initialSorts)
  );
  return {
    sortBy: createSortBy(setActiveSortList),
    sortState: createSortState(activeSorts),
    activeSorts,
    sortedItems: sort(compareFunctions, activeSorts, items)
  };
}
useSorted.defaultCompare = defaultCompare;

// src/user-interface-state.ts
import Cookies from "js-cookie";
var cookieName = "bmt_user_states";
var cookieExpirationDays = 365;
var UserInterfaceState = class {
  constructor(userId) {
    __publicField(this, "userId");
    __publicField(this, "userCookies");
    __publicField(this, "cookies");
    this.userId = userId;
    this.cookies = this.getCookies;
    this.userCookies = this.getUserCookies;
  }
  setStateFor(feature, value) {
    this.cookies = this.getCookies;
    this.userCookies = this.getUserCookies;
    this.cookies[`user_${this.userId}`] = {
      ...this.userCookies,
      ...{ [feature]: value }
    };
    Cookies.set(cookieName, this.cookies, { expires: cookieExpirationDays });
  }
  getStateFor(feature) {
    this.cookies = this.getCookies;
    this.userCookies = this.getUserCookies;
    return this.userCookies[feature];
  }
  get getCookies() {
    return Cookies.getJSON(cookieName) || {};
  }
  get getUserCookies() {
    return this.cookies[`user_${this.userId}`] || {};
  }
};

// src/graphql/hooks/useGraphQLMutation.ts
import {
  useMutation as useMutation2
} from "@apollo/client";

// src/graphql/apolloUtils.ts
import { Kind, OperationTypeNode } from "graphql";
import merge from "lodash/merge";
function getMutationName(mutation) {
  const mutationOperationDefinition = mutation.definitions.find(
    (definition) => definition.kind === Kind.OPERATION_DEFINITION && definition.operation === OperationTypeNode.MUTATION
  );
  return mutationOperationDefinition?.name?.value;
}
function mergeHeaders(mutationFunctionOptions, headers) {
  return merge(
    {
      context: {
        headers
      }
    },
    mutationFunctionOptions
  );
}

// src/graphql/idempotency/IdempotencyProvider.tsx
import { createContext as createContext2, useContext as useContext2, useRef as useRef3 } from "react";
import { jsx as jsx10 } from "react/jsx-runtime";
var IdempotencyContext = createContext2({});
var createIdempotencyTokenRegistry = (featureName, featureRegistry) => {
  const featureMutationDictionary = featureRegistry[featureName];
  if (featureMutationDictionary === void 0) {
    throw new Error(
      `createIdempotencyTokenRegistry was unable to find feature ${featureName} in the feature registry. Make sure to register your feature in the client's feature-registry.ts.`
    );
  }
  const idempotencyTokenRegistry = {};
  Object.entries(featureMutationDictionary).forEach(
    ([mutationName, { idempotencyToken: IdempotencyToken2 }]) => {
      idempotencyTokenRegistry[mutationName] = new IdempotencyToken2();
    }
  );
  return idempotencyTokenRegistry;
};
var IdempotencyProvider = ({ children, featureName, featureRegistry }) => {
  const idempotencyTokenRegistry = useRef3(
    createIdempotencyTokenRegistry(featureName, featureRegistry)
  );
  return /* @__PURE__ */ jsx10(IdempotencyContext.Provider, { value: idempotencyTokenRegistry.current, children });
};
var useFeatureToken = (mutationName) => {
  const idempotencyTokenRegistry = useContext2(IdempotencyContext);
  const featureToken = idempotencyTokenRegistry[mutationName];
  if (featureToken === void 0) {
    throw new Error(
      `useFeatureToken was unable to find feature ${mutationName} in the feature registry. Make sure to register your feature in the client's feature-registry.ts.`
    );
  }
  return featureToken;
};

// src/graphql/hooks/useGraphQLMutation.ts
var useGraphQLMutation = (mutation, mutationHookOptions = {}) => {
  const mutationName = getMutationName(mutation);
  if (mutationName === void 0) {
    throw new Error(
      `useGraphQLMutation was unable to resolve a mutation name in the provided mutation document. Make sure to provide a valid mutation document.`
    );
  }
  const featureToken = useFeatureToken(mutationName);
  const [mutationFunction, mutationResult] = useMutation2(mutation, mutationHookOptions);
  const idempotentMutationFunction = (mutationFunctionOptions = {}) => {
    const idempotencyHeaders = featureToken.prepareHeaders();
    const idempotentMutationFunctionOptions = mergeHeaders(
      mutationFunctionOptions,
      idempotencyHeaders
    );
    const mutatePromise = mutationFunction(idempotentMutationFunctionOptions);
    mutatePromise.then(() => {
      featureToken.refresh();
    }).catch(() => null);
    return mutatePromise;
  };
  return [idempotentMutationFunction, mutationResult];
};

// src/graphql/hooks/useGraphQLQuery.ts
import {
  useQuery as useQuery2
} from "@apollo/client";
var useGraphQLQuery = (query, options) => {
  const queryResult = useQuery2(query, options);
  const shouldThrowOnError = options?.throwOnError ?? false;
  if (shouldThrowOnError && queryResult.error) throw queryResult.error;
  return queryResult;
};

// src/graphql/hooks/useGraphQLSuspenseQuery.ts
import { useSuspenseQuery } from "@apollo/client";

// src/graphql/idempotency/idempotencyToken.ts
import { formatRFC7231 } from "date-fns";
import { v4 as uuidv4 } from "uuid";
var IdempotencyToken = class {
  constructor() {
    __publicField(this, "key");
    __publicField(this, "firstSent");
  }
  getKey() {
    return this.key;
  }
  prepareHeaders() {
    if (this.firstSent === void 0) {
      this.firstSent = formatRFC7231(/* @__PURE__ */ new Date());
    }
    return {
      "Idempotency-Key": this.key,
      "Idempotency-First-Sent": this.firstSent
    };
  }
};
var RefreshableIdempotencyToken = class extends IdempotencyToken {
  constructor() {
    super();
    this.key = uuidv4();
  }
  refresh() {
    this.key = uuidv4();
    this.firstSent = void 0;
  }
};
var SingleUseIdempotencyToken = class extends IdempotencyToken {
  constructor() {
    super();
    this.key = uuidv4();
  }
  refresh() {
    return null;
  }
};
var EditableFormIdempotencyToken = class extends RefreshableIdempotencyToken {
};
var FlowSubmissionIdempotencyToken = class extends SingleUseIdempotencyToken {
};
var SendEmailIdempotencyToken = class extends RefreshableIdempotencyToken {
};
var ToggleIdempotencyToken = class extends RefreshableIdempotencyToken {
};
var ValidationIdempotencyToken = class extends RefreshableIdempotencyToken {
};

// src/graphql/client/links/retry-link.ts
import { RetryLink } from "@apollo/client/link/retry";
var NO_RETRY_CODES = /* @__PURE__ */ new Set([400, 401, 403, 409, 422]);
var retryLink = new RetryLink({
  delay: {
    initial: 300,
    jitter: true,
    max: Infinity
  },
  attempts: {
    max: 5,
    retryIf: (error) => {
      return !!error && !NO_RETRY_CODES.has(error.statusCode ?? -1);
    }
  }
});

// src/use-script.ts
import {
  useEffect as useEffect7,
  useRef as useRef4,
  useState as useState5
} from "react";
var Status = /* @__PURE__ */ ((Status2) => {
  Status2["IDLE"] = "idle";
  Status2["LOADING"] = "loading";
  Status2["READY"] = "ready";
  Status2["ERROR"] = "error";
  return Status2;
})(Status || {});
var Nodes = /* @__PURE__ */ ((Nodes2) => {
  Nodes2["BODY"] = "body";
  Nodes2["HEAD"] = "head";
  return Nodes2;
})(Nodes || {});
function useScript(src, options) {
  const scriptRef = useRef4(null);
  const [status, setStatus] = useState5(src ? "loading" /* LOADING */ : "idle" /* IDLE */);
  const attrs = options?.attributes ?? {};
  const tagAttributes = JSON.stringify(attrs);
  const domNode = options?.domNode ?? "head" /* HEAD */;
  useEffect7(() => {
    if (!src) {
      return setStatus("idle" /* IDLE */);
    }
    let script = document.querySelector(
      `script[src="${src}"]`
    );
    if (!script) {
      script = document.createElement("script");
      script.src = src;
      script.setAttribute("data-status", "loading" /* LOADING */);
      const attributes = JSON.parse(tagAttributes);
      Object.keys(attributes).forEach(
        (key) => {
          script.setAttribute(key, attributes[key]);
        }
      );
      script.onerror = () => script?.setAttribute("data-status", "error" /* ERROR */);
      script.onload = () => script?.setAttribute("data-status", "ready" /* READY */);
      document[domNode].appendChild(script);
    } else {
      const currentStatus = script.getAttribute("data-status") ?? "loading" /* LOADING */;
      setStatus(currentStatus);
    }
    const setStateFromEvent = (event) => {
      setStatus(event.type === "load" ? "ready" /* READY */ : "error" /* ERROR */);
    };
    script.addEventListener("load", setStateFromEvent);
    script.addEventListener("error" /* ERROR */, setStateFromEvent);
    return () => {
      script?.removeEventListener("load", setStateFromEvent);
      script?.removeEventListener("error" /* ERROR */, setStateFromEvent);
      if (options?.isScriptRemovable) {
        script?.remove();
      }
    };
  }, [src, domNode, tagAttributes]);
  return { status, scriptRef };
}

// src/use-toggled-value.ts
import { useReducer as useReducer3 } from "react";
function useToggledValue(initialState) {
  const [isValueToggled, toggleValue] = useReducer3(
    (state3) => !state3,
    initialState
  );
  return [isValueToggled, toggleValue];
}

// src/cache-worker.ts
import _flatten from "lodash/flatten";
import _zip from "lodash/zip";
function worker(strings, ...interpolatedValues) {
  const script = _flatten(_zip(strings, interpolatedValues)).join("");
  const url = URL.createObjectURL(
    new Blob([script], { type: "text/javascript" })
  );
  return new Worker(url);
}
function createHandler(self) {
  return function eventHandler(event) {
    if (event.data.type === "cacheResponse") {
      const now = Number(/* @__PURE__ */ new Date());
      const expiredKeys = Object.keys(event.data.cache).reduce(
        (result, key) => {
          if (key.substring(0, key.indexOf(".")) === "bmt") {
            const value = JSON.parse(event.data.cache[key]);
            if (value.expiry && now > value.expiry) {
              return result.concat(key);
            }
            return result;
          }
          return result;
        },
        []
      );
      self.postMessage({ type: "clearKeys", keys: expiredKeys });
    }
  };
}
function startCacheWorker() {
  const cacheWorker = worker`
    self.onmessage = ${createHandler.toString()}(self)

    var interval = setInterval(function () {
      self.postMessage({ type: 'checkCache' });
    }, 1000 * 60 * 10);

    self.postMessage({ type: 'checkCache' });
  `;
  cacheWorker.addEventListener("message", ({ data }) => {
    if (data.type === "checkCache") {
      const cache = Object.keys(sessionStorage).reduce(
        (result, key) => ({ ...result, [key]: sessionStorage.getItem(key) }),
        {}
      );
      cacheWorker.postMessage({ type: "cacheResponse", cache });
    }
    if (data.type === "clearKeys") {
      data.keys.forEach((key) => sessionStorage.removeItem(key));
    }
  });
  cacheWorker.onerror = () => {
    throw new Error("Cache worker encountered an error");
  };
}
export {
  DataDogInitializer,
  DataFetchProvider,
  DateFormat,
  EditableFormIdempotencyToken,
  FlowContextProvider,
  FlowContextProviderCompat,
  FlowSessionRoutes,
  FlowSessionRoutesCompat,
  FlowSubmissionIdempotencyToken,
  IdempotencyProvider,
  IdempotencyToken,
  MoneyFormat,
  Nodes,
  Redirect,
  RedirectCompat,
  test_helpers_exports as RuntimeTesting,
  SERVICE_UNAVAILABLE_ERROR,
  SendEmailIdempotencyToken,
  SentryInitializer,
  SessionSync,
  Status,
  StepWrapper,
  StepWrapperCompat,
  StimulusAppBootstrapper,
  ToggleIdempotencyToken,
  UserInterfaceState,
  ValidationIdempotencyToken,
  camelizeKeys,
  createHandler,
  formatDate,
  formatMoney,
  getClient,
  getCsrfToken,
  isEligibleSpaceKeydown,
  isEnterKeydown,
  isKeyboardSelection,
  isNotFound,
  isServiceUnavailable,
  isSpaceKeydown,
  isSpacebarSelectableElement,
  removeTrailingSlashes,
  retryLink,
  route,
  sanitizeUrlParam,
  sanitizedPath,
  sanitizedTokenQuery,
  sanitizedUrl,
  segmentInitializer,
  setTraits,
  snakeizeKeys,
  startAnalytics,
  startCacheWorker,
  toCentsFromString,
  toMoney,
  track,
  useDeepMemo,
  useExitToPath,
  useExitToPathCompat,
  useFeatureToken,
  useFlowState,
  useGraphQLMutation,
  useGraphQLQuery,
  useSuspenseQuery as useGraphQLSuspenseQuery,
  useMatchMedia,
  useMutation,
  useQuery,
  useQueryParams,
  useQueryParamsCompat,
  useReturnToPath,
  useReturnToPathCompat,
  useScript,
  useSessionStorage,
  useSorted,
  useToggledValue,
  when
};
//# sourceMappingURL=index.mjs.map